/**
 * VariantImpactDialog - Confirmation dialog listing the variants an axis edit will touch
 *
 * Both AxisEditor and PatternSelector ask the same question twice (delete a value,
 * or assign a first value to variants that have none), each time with the same
 * thumbnail list. One component keeps those four dialogs identical.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { VariantV3 } from '../../types';
import { formatAxisValueForDisplay, isDefaultAxisValue } from '../../types';

// =============================================================================
// Types
// =============================================================================

interface VariantImpactDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog is dismissed (backdrop, escape, cancel) */
  onCancel: () => void;
  /** Called when the confirm button is pressed */
  onConfirm: () => void;
  /** Dialog title */
  title: string;
  /** Explanatory message */
  message: string;
  /** Label of the confirm button */
  confirmLabel: string;
  /** Destructive confirmations are outlined red, never filled */
  destructive?: boolean;
  /** Variants impacted by the action */
  variants: VariantV3[];
  /** Whether unset axes are skipped when building a variant's display name */
  skipDefaultAxisValues?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export const VariantImpactDialog: React.FC<VariantImpactDialogProps> = ({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel,
  destructive = false,
  variants,
  skipDefaultAxisValues = false,
}) => {
  const { t } = useTranslation();

  const formatVariantDisplayName = (variant: VariantV3): string => {
    const parts: string[] = [];
    Object.entries(variant.axes).forEach(([_, axisValue]) => {
      if (skipDefaultAxisValues && isDefaultAxisValue(axisValue)) return;
      parts.push(formatAxisValueForDisplay(axisValue));
    });
    return parts.join(' / ');
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[250px] overflow-y-auto border bg-muted/40 p-1">
          {variants.map((variant) => (
            <div
              key={variant.uuid}
              className="flex items-center gap-3 border-b px-2 py-2 last:border-b-0"
            >
              <div className="size-10 shrink-0 overflow-hidden border bg-muted">
                {variant.visualization?.thumbnailPath ? (
                  <img
                    src={variant.visualization.thumbnailPath}
                    alt=""
                    className="size-full object-cover"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center text-[10px] text-muted-foreground">
                    —
                  </div>
                )}
              </div>
              <p className="flex-1 text-xs text-foreground">{formatVariantDisplayName(variant)}</p>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('CANCEL', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant={destructive ? 'outline' : 'default'}
            onClick={onConfirm}
            className={cn(
              destructive &&
                'border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive'
            )}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VariantImpactDialog;
